import React, { useEffect, useState } from "react";
import { AlertTriangle, ExternalLink, ChevronDown } from "lucide-react";
import { getPlaceAlerts } from "@/lib/api";
import { T } from "@/context/LanguageContext";

// Severity → banner treatment.
const SEV = {
  Extreme: "border-red-600 bg-red-50 text-red-900",
  Severe: "border-red-500 bg-red-50 text-red-900",
  Moderate: "border-amber-500 bg-amber-50 text-amber-900",
  Minor: "border-blue-500 bg-blue-50 text-blue-900",
  Unknown: "border-slate-400 bg-slate-50 text-slate-800",
};

function AlertCard({ a }) {
  const [open, setOpen] = useState(false);
  const tone = SEV[a.severity] || SEV.Unknown;
  return (
    <div className={`rounded-xl border-l-4 ${tone} px-4 py-3`} data-testid="place-alert">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-2 text-left"
        data-testid="place-alert-toggle"
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span className="flex-1">
          <span className="block text-sm font-bold">{a.event}</span>
          {a.headline && <span className="mt-0.5 block text-xs opacity-90">{a.headline}</span>}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-2 border-t border-current/15 pt-2 text-xs leading-relaxed">
          {a.description && <p className="whitespace-pre-line opacity-90">{a.description}</p>}
          {a.instruction && (
            <p className="font-semibold"><T>What to do</T>: {a.instruction}</p>
          )}
          {a.expires && (
            <p className="opacity-75">
              <T>In effect until</T> {new Date(a.expires).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Prominent banner of active NWS watches/warnings/advisories (official source). */
export function PlaceAlerts({ geoId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    getPlaceAlerts(geoId).then((d) => { if (alive) setData(d); }).catch(() => {});
    return () => { alive = false; };
  }, [geoId]);

  if (!data?.alerts?.length) return null;

  return (
    <section className="mx-auto max-w-[1500px] px-4 pt-4" data-testid="place-alerts">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-red-700">
          <AlertTriangle className="h-3.5 w-3.5" /> <T>Active Alerts</T>
        </div>
        <a
          href={data.source?.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground hover:text-navy"
          data-testid="place-alerts-source"
        >
          {data.source?.label} <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <div className="space-y-2">
        {data.alerts.map((a, i) => <AlertCard key={i} a={a} />)}
      </div>
    </section>
  );
}

export default PlaceAlerts;
